import { css, html, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { LabeledElement } from '../../abstracts/labeled-element.js';
import { t, tArray } from 'src/i18n';
import { DayOfWeek, LocalDate } from 'src/utilities/local-date.js';
import Pikaday from 'pikaday';

/**
 *
 * @fires value-changed - Dispatched immediately after changes by the user
 *
 * STATUS OK
 */
@customElement('d-select-date')
export class DSelectDate extends LabeledElement {
  static readonly styles = [
    ...LabeledElement.styles,
    css`
      :host {
        display: block;
      }

      .container {
        display: flex;
        align-items: center;
      }

      :host([invalid]) input {
        border: 1px solid var(--alertColor);
        margin-left: -1px;
        margin-right: -1px;
      }

      :host([controller]) input {
        padding-right: 6px;
        padding-left: 7px;
      }

      /*!
         * Pikaday
         * Copyright © 2014 David Bushell | BSD & MIT license | https://dbushell.com/
         */

      .pika-single {
        z-index: 9999;
        display: block;
        position: relative;
        color: #333;
        background: #fff;
        border: 1px solid #ccc;
        border-bottom-color: #bbb;
        font-family: var(--mainSans);
      }

      /*
        clear child float (pika-lendar), using the famous micro clearfix hack
        http://nicolasgallagher.com/micro-clearfix-hack/
        */
      .pika-single:before,
      .pika-single:after {
        content: ' ';
        display: table;
      }

      .pika-single:after {
        clear: both;
      }

      .pika-single.is-hidden {
        display: none;
      }

      .pika-single.is-bound {
        position: absolute;
        box-shadow: 0 5px 15px -5px rgba(0, 0, 0, 0.5);
      }

      .pika-lendar {
        float: left;
        width: 240px;
        margin: 8px;
      }

      .pika-title {
        position: relative;
        text-align: center;
      }

      .pika-label {
        display: inline-block;
        position: relative;
        z-index: 9999;
        overflow: hidden;
        margin: 0;
        border: 1px solid var(--borderColor);
        border-radius: 4px;
        padding: 5px 7px;
        font-size: 14px;
        line-height: 20px;
        font-weight: bold;
        background-color: #fff;
      }

      .pika-title .pika-label + .pika-label {
        margin-left: 4px;
      }

      .pika-label:after {
        display: inline-block;
        content: '';
        margin-left: 4px;
        width: 10px;
        height: 10px;
        background: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAeCAYAAAAsEj5rAAAAUklEQVR42u3VMQoAIBADQf8Pgj+OD9hG2CtONJB2ymQkKe0HbwAP0xucDiQWARITIDEBEnMgMQ8S8+AqBIl6kKgHiXqQqAeJepBo/z38J/U0uAHlaBkBl9I4GwAAAABJRU5ErkJggg==)
          50% 50% no-repeat;
        background-size: 140% 140%;
        transform: rotate(270deg);
        opacity: 0.5;
      }

      .pika-title select {
        cursor: pointer;
        position: absolute;
        z-index: 9998;
        margin: 0;
        left: 0;
        top: 5px;
        opacity: 0;
      }

      .pika-prev,
      .pika-next {
        display: block;
        cursor: pointer;
        position: relative;
        outline: none;
        border: 0;
        padding: 0;
        width: 20px;
        height: 30px;
        /* hide text using text-indent trick, using width value (it's enough) */
        text-indent: 20px;
        white-space: nowrap;
        overflow: hidden;
        background-color: transparent;
        background-position: center center;
        background-repeat: no-repeat;
        background-size: 75% 75%;
        opacity: 0.5;
      }

      .pika-prev:hover,
      .pika-next:hover {
        opacity: 1;
      }

      .pika-prev,
      .is-rtl .pika-next {
        float: left;
        background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAeCAYAAAAsEj5rAAAAUklEQVR42u3VMQoAIBADQf8Pgj+OD9hG2CtONJB2ymQkKe0HbwAP0xucDiQWARITIDEBEnMgMQ8S8+AqBIl6kKgHiXqQqAeJepBo/z38J/U0uAHlaBkBl9I4GwAAAABJRU5ErkJggg==');
      }

      .pika-next,
      .is-rtl .pika-prev {
        float: right;
        background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAeCAYAAAAsEj5rAAAAU0lEQVR42u3VOwoAMAgE0dwfAnNjU26bYkBCFGwfiL9VVWoO+BJ4Gf3gtsEKKoFBNTCoCAYVwaAiGNQGMUHMkjGbgjk2mIONuXo0nC8XnCf1JXgArVIZAQh5TKYAAAAASUVORK5CYII=');
      }

      .pika-prev.is-disabled,
      .pika-next.is-disabled {
        cursor: default;
        opacity: 0.2;
      }

      .pika-select {
        display: none;
      }

      .open .pika-select {
        display: inline-block;
      }

      .pika-table {
        width: 100%;
        border-collapse: collapse;
        border-spacing: 0;
        border: 0;
      }

      .pika-table th,
      .pika-table td {
        width: 14.285714285714286%;
        padding: 0;
      }

      .pika-table th {
        color: #999;
        font-size: 12px;
        line-height: 25px;
        font-weight: bold;
        text-align: center;
      }

      .pika-button {
        cursor: pointer;
        display: block;
        box-sizing: border-box;
        -moz-box-sizing: border-box;
        outline: none;
        border: 0;
        margin: 0;
        width: 100%;
        padding: 5px;
        font-family: var(--mainSans);
        color: #666;
        font-size: 12px;
        line-height: 15px;
        text-align: right;
        background: #f5f5f5;
      }

      .pika-week {
        font-size: 11px;
        color: #999;
      }

      .is-today .pika-button {
        color: var(--themeColor);
        font-weight: bold;
      }

      .is-selected .pika-button,
      .has-event .pika-button {
        color: #fff;
        font-weight: bold;
        background: var(--themeColor);
        box-shadow: inset 0 1px 3px var(--themeColorDarkerOne);
        border-radius: 3px;
      }

      .has-event .pika-button {
        background: #005da9;
        box-shadow: inset 0 1px 3px #0076c9;
      }

      .is-disabled .pika-button,
      .is-inrange .pika-button {
        background: #d5e9f7;
      }

      .is-startrange .pika-button {
        color: #fff;
        background: #6cb31d;
        box-shadow: none;
        border-radius: 3px;
      }

      .is-endrange .pika-button {
        color: #fff;
        background: #33aaff;
        box-shadow: none;
        border-radius: 3px;
      }

      .is-disabled .pika-button {
        pointer-events: none;
        cursor: default;
        color: #999;
        opacity: 0.3;
      }

      .is-outside-current-month .pika-button {
        color: #999;
        opacity: 0.3;
      }

      .is-selection-disabled {
        pointer-events: none;
        cursor: default;
      }

      .pika-button:hover,
      .pika-row.pick-whole-week:hover .pika-button {
        color: #fff;
        background: var(--themeColorLighterTwo);
        box-shadow: none;
        border-radius: 3px;
      }

      /* styling for abbr */
      .pika-table abbr {
        border-bottom: none;
        text-decoration: none;
        cursor: default;
      }

      .wrapper {
        position: relative;
        width: 104px;
      }

      :host([controller]) .wrapper {
        width: 108px;
      }

      :host([clear]) .wrapper {
        width: 116px;
      }

      :host([clear][controller]) .wrapper {
        width: 120px;
      }

      .today {
        display: none;
      }

      :host([today]) .today {
        display: block;
      }

      dialog {
        padding: 0;
        border: none !important;
        /* !important used here to override polyfill CSS, if loaded */
        position: relative;
        top: 0;
        left: 0;
        margin: 0;
        box-shadow: 0 16px 24px 2px hsla(0, 0%, 0%, 0.3);
      }
    `,
  ];
  @query('#p')
  input!: HTMLElement;
  @query('#c')
  container!: HTMLElement;
  @query('dialog')
  dialog!: HTMLDialogElement;
  @property({ type: String })
  value = '';
  @property({ type: Boolean, reflect: true })
  invalid = false;
  @property({ type: Array })
  disabledPeriods: { start: LocalDate; end: LocalDate }[] = [];
  @property({ type: String })
  disableBefore = '';
  @property({ type: String })
  disableAfter = '';
  @property({ type: Boolean })
  disableMondays = false;
  @property({ type: Boolean })
  disableTuesdays = false;
  @property({ type: Boolean })
  disableWednesdays = false;
  @property({ type: Boolean })
  disableThursdays = false;
  @property({ type: Boolean })
  disableFridays = false;
  @property({ type: Boolean })
  disableSaturdays = false;
  @property({ type: Boolean })
  disableSundays = false;
  @property({ type: Boolean, attribute: 'clear', reflect: true })
  clear = false;
  @property({ type: Boolean, attribute: 'today', reflect: true })
  today = false;
  @query('.wrapper')
  _inputWrapper!: HTMLDivElement;
  @query('#m')
  m!: HTMLDivElement;
  private instance: Pikaday | undefined;

  private get valueDisplay() {
    return !this.value || this.value === '' ? '' : LocalDate.fromString(this.value).toStringForDisplay();
  }

  private get showClear() {
    return this.clear && this.value !== '';
  }

  firstUpdated() {
    const opts: Pikaday.PikadayOptions = {
      field: this.input,
      bound: false,
      position: 'bottom left',
      reposition: true,
      container: this.container,
      format: 'DD.MM.YYYY',
      defaultDate: undefined,
      setDefaultDate: false,
      firstDay: 1,
      minDate: undefined,
      maxDate: undefined,
      yearRange: 10,
      showWeekNumber: true,
      isRTL: false,
      yearSuffix: '',
      showMonthAfterYear: false,
      numberOfMonths: 1,
      mainCalendar: 'left',
      keyboardInput: false,
      blurFieldOnSelect: true,
      toString(date: Date, format?: string): string {
        const localDate = LocalDate.fromDate(date);
        if (format === 'DD.MM.YYYY') {
          return localDate.toStringForDisplay();
        } else {
          return localDate.toString();
        }
      },
      onSelect: (date) => {
        this.value = LocalDate.fromDate(date).toString();
        this.dialog.close();
        this.container.classList.remove('open');
        this.dispatchEvent(
          new CustomEvent('value-changed', {
            bubbles: true,
            composed: true,
            detail: { value: this.value },
          }),
        );
      },
      parse(date: string, format: string): Date | null {
        if (date !== '' && format === 'DD.MM.YYYY') {
          const s = date.split('.');
          const x = s[2] + '-' + s[1] + '-' + s[0];
          return new Date(x);
        }
        return null;
      },
      disableDayFn: (d) => {
        const date = LocalDate.fromDate(d);

        //Prevents period overlap by disabling// Prevents period overlap by disabling
        // all dates within and before registered periods before preset date,
        //and all dates within and after registered periods after preset date
        const periods = this.disabledPeriods ?? [];
        for (const item of periods) {
          const start = item.start;
          const end = item.end;
          if (date.isSameOrAfter(start) && date.isSameOrBefore(end)) {
            return true;
          }
        }
        if (this.disableBefore && date.isBefore(LocalDate.fromString(this.disableBefore))) {
          return true;
        }
        if (this.disableAfter && date.isAfter(LocalDate.fromString(this.disableAfter))) {
          return true;
        }
        if (this.disableMondays && date.dayOfWeek() === DayOfWeek.MONDAY) {
          return true;
        }
        if (this.disableTuesdays && date.dayOfWeek() === DayOfWeek.TUESDAY) {
          return true;
        }
        if (this.disableWednesdays && date.dayOfWeek() === DayOfWeek.WEDNESDAY) {
          return true;
        }
        if (this.disableThursdays && date.dayOfWeek() === DayOfWeek.THURSDAY) {
          return true;
        }
        if (this.disableFridays && date.dayOfWeek() === DayOfWeek.FRIDAY) {
          return true;
        }
        if (this.disableSaturdays && date.dayOfWeek() === DayOfWeek.SATURDAY) {
          return true;
        }
        return this.disableSundays && date.dayOfWeek() === DayOfWeek.SUNDAY;
      },
      i18n: {
        previousMonth: t('library.selectDate.previousMonth'),
        nextMonth: t('library.selectDate.nextMonth'),
        months: tArray('library.selectDate.months'),
        weekdays: tArray('library.selectDate.weekdays'),
        weekdaysShort: tArray('library.selectDate.weekdaysShort'),
      },
    };
    this.instance = new Pikaday(opts);
  }

  _onDialogClick(e) {
    const rect = this.dialog.getBoundingClientRect();
    const clickedInDialog =
      rect.top <= e.clientY &&
      e.clientY <= rect.top + rect.height &&
      rect.left <= e.clientX &&
      e.clientX <= rect.left + rect.width;

    if (!clickedInDialog) {
      this.dialog.close();
      this.container.classList.remove('open');
    }
  }

  renderContent() {
    return html`
      <div class="container">
        <div class="wrapper">
          <input
            id="p"
            class="flex"
            readonly
            ?disabled=${this.disabled}
            tabindex="1"
            type="text"
            @click=${() => this.showCalendar()}
            .value=${this.valueDisplay}
          />
          ${this.showClear ? html` <div class="clear" @click=${this.onClear}></div> ` : nothing}
        </div>
        <d-action class="today" @click=${this.onToday}>${t('library.selectDate.setToday')}</d-action>
      </div>
      <dialog @click=${this._onDialogClick}>
        <div id="c"></div>
      </dialog>
    `;
  }

  updated() {
    this.instance?.draw(true);
  }

  private showCalendar() {
    const inputRect = this._inputWrapper.getBoundingClientRect();
    let left: number;
    let top: number;

    this.dialog.showModal();
    this.container.classList.add('open');
    const dialogRect = this.dialog.getBoundingClientRect();

    if (inputRect.y + inputRect.height + dialogRect.height < document.body.offsetHeight) {
      left = inputRect.left + window.scrollX;
      top = inputRect.bottom + window.scrollY;
    } else {
      left = inputRect.left + window.scrollX;
      top = inputRect.top - dialogRect.height + window.scrollX;
    }

    if (inputRect.x + inputRect.width + dialogRect.width > document.body.offsetWidth) {
      left = inputRect.left - (dialogRect.width - inputRect.width);
    }

    if (left < 0) {
      left = 0;
    }

    this.dialog.style.left = left + 'px';
    this.dialog.style.top = top + 'px';
  }

  private onToday() {
    this.value = LocalDate.fromString().toString();
    this.dispatchEvent(
      new CustomEvent('value-changed', {
        bubbles: true,
        composed: true,
        detail: { value: this.value },
      }),
    );
  }

  private onClear() {
    if (!this.disabled) {
      this.value = '';
      this.dispatchEvent(
        new CustomEvent('value-changed', {
          bubbles: true,
          composed: true,
          detail: { value: this.value },
        }),
      );
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-select-date': DSelectDate;
  }
}
